import { Check, CreditCard, Loader2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useUserProfile } from '../context/UserProfileContext'
import paymentService from '../utils/paymentService'
import { ApiError } from '../utils/api'

interface PricingPlan {
  _id: string
  name: string
  slug: string
  productType: string
  price: number
  currency: string
  billingCycle: string
  features: string[]
  isActive: boolean
  sortOrder?: number
}

interface SelfAssessmentPlanSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  formId?: string | undefined // Optional - will be created if not provided in selectOnly mode
  onPlanSelected?: (slug: string, assessmentId?: string) => void | Promise<void>
  selectOnly?: boolean
  nino?: string // Required for creating new assessment in selectOnly mode
  onAssessmentCreated?: (assessmentId: string) => void
}

export default function SelfAssessmentPlanSelectionModal({
  isOpen,
  onClose,
  formId,
  onPlanSelected,
  selectOnly = false,
}: SelfAssessmentPlanSelectionModalProps) {
  const { user } = useUserProfile()
  const [plans, setPlans] = useState<PricingPlan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedPlanSlug, setSelectedPlanSlug] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      fetchSelfAssessmentPlans()
    }
  }, [isOpen])

  const fetchSelfAssessmentPlans = async () => {
    try {
      setIsLoading(true)
      const response = await paymentService.listPricingPlans()
      
      // Extract Self Assessment plans from response
      const saPlans: PricingPlan[] = (response as any)?.['self-assessment'] || []
      
      // Sort by price or sortOrder
      const sortedPlans = saPlans.sort((a: PricingPlan, b: PricingPlan) => {
        if (a.sortOrder && b.sortOrder) {
          return a.sortOrder - b.sortOrder
        }
        return a.price - b.price
      })

      setPlans(sortedPlans)
      
      // Pre-select first plan if available
      if (sortedPlans.length > 0 && sortedPlans[0]) {
        setSelectedPlanSlug(sortedPlans[0].slug)
      }
    } catch (error) {
      console.error('Failed to fetch Self Assessment plans:', error)
      toast.error('Unable to load pricing plans. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePlanSelection = (slug: string) => {
    setSelectedPlanSlug(slug)

    // In select-only mode, we just select but don't proceed automatically
    // User must click "Select Plan" button
  }

  const handleProceed = async (slugOverride?: string) => {
    const slugToUse = slugOverride || selectedPlanSlug

    if (!slugToUse) {
      toast.error('Please select a plan to continue')
      return
    }

    // If selectOnly mode, hand the chosen plan back to the parent, which decides
    // what happens next (e.g. redirect to checkout). We stay neutral on messaging.
    if (selectOnly) {
      setIsProcessing(true)
      try {
        if (onPlanSelected) {
          await onPlanSelected(slugToUse)
        }
        // Parent handles the next step and any user-facing messaging.
      } catch (err) {
        console.error('Plan selection failed:', err)
        toast.error('Something went wrong. Please try again.')
        setIsProcessing(false)
      }
      return
    }

    // For payment mode, formId is required
    if (!formId) {
      toast.error('Assessment ID is required for payment')
      return
    }

    // Proceed to checkout
    try {
      setIsProcessing(true)

      // Get current URL params to pass context forward
      const urlParams = new URLSearchParams(window.location.search)
      const nino = urlParams.get('nino') || ''
      const taxYear = urlParams.get('taxYear') || ''
      const calculationId = urlParams.get('calculationId') || localStorage.getItem(`calculationId-${formId}`) || ''
      
      const successUrl = `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}&plan=${selectedPlanSlug}&formId=${formId}&nino=${nino}&taxYear=${taxYear}&calculationId=${calculationId}`
      const cancelUrl = `${window.location.origin}/add-self-assessment?nino=${nino}&taxYear=${taxYear}&assessmentId=${formId}`

      const checkoutData = await paymentService.createCheckout(slugToUse, {
        successUrl,
        cancelUrl,
        customerEmail: user?.email || '',
        formId,
      })

      if (!checkoutData.url) {
        throw new Error('Invalid checkout session URL')
      }

      // Redirect to Stripe checkout
      window.location.href = checkoutData.url
    } catch (err) {
      console.error('Checkout failed:', err)
      if (err instanceof ApiError) {
        toast.error(err.message || 'Unable to start checkout. Please try again.')
      } else {
        toast.error('Unable to start checkout. Please try again.')
      }
      setIsProcessing(false)
    }
  }

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(price)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Select Self Assessment Plan
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose the plan that best fits your needs
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-red-600" />
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No Self Assessment plans available at the moment.</p>
              <button
                onClick={onClose}
                className="mt-4 text-red-600 hover:text-red-700 font-medium"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {plans.map((plan) => (
                <div
                  key={plan.slug}
                  role="button"
                  tabIndex={0}
                  onClick={() => handlePlanSelection(plan.slug)}
                  onKeyDown={(e) => e.key === 'Enter' && handlePlanSelection(plan.slug)}
                  className={`
                    relative border-2 rounded-xl p-6 cursor-pointer transition-all
                    ${
                      selectedPlanSlug === plan.slug
                        ? 'border-red-600 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  {/* Selection indicator */}
                  <div className="absolute top-4 right-4">
                    <div
                      className={`
                        h-6 w-6 rounded-full flex items-center justify-center
                        ${
                          selectedPlanSlug === plan.slug
                            ? 'bg-red-600'
                            : 'border-2 border-gray-300'
                        }
                      `}
                    >
                      {selectedPlanSlug === plan.slug && (
                        <Check className="h-4 w-4 text-white" />
                      )}
                    </div>
                  </div>

                  {/* Plan details */}
                  <div className="pr-10">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {plan.name}
                    </h3>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-gray-900">
                        {formatPrice(plan.price, plan.currency)}
                      </span>
                      <span className="text-sm text-gray-600">
                        {plan.billingCycle}
                      </span>
                    </div>

                    {/* Features */}
                    <ul className="mt-4 space-y-2">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                          <Check className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Additional info */}
          {plans.length > 0 && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> {selectOnly 
                  ? 'Select your plan now. Payment will be required when you submit your return to HMRC.'
                  : 'Payment is required to submit your Self Assessment return to HMRC. Your data is saved and will be available after payment.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {plans.length > 0 && (
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={() => void handleProceed()}
              disabled={!selectedPlanSlug || isProcessing}
              className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {selectOnly ? 'Creating Assessment...' : 'Processing...'}
                </>
              ) : selectOnly ? (
                <>
                  <Check className="h-5 w-5" />
                  Select Plan & Continue
                </>
              ) : (
                <>
                  <CreditCard className="h-5 w-5" />
                  Continue to Payment
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
